import { useMemo, useState } from 'react';
import { CaretDown, Check } from '@phosphor-icons/react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { formatDateTime } from '@/utils/datetime';

export const yen = (n) => (n == null ? '—' : '¥' + Number(n).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' }));
export const jd = (iso) => (iso ? String(iso).slice(0, 10).replaceAll('-', '/') : '—');
export const jdt = (iso) => (iso ? formatDateTime(iso) : '—');

export const FULFILLMENT = {
  production: { label: '製造', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  stock: { label: '在庫出荷', cls: 'bg-teal-50 text-teal-700 border-teal-200' },
};

export const APPROVAL_METHODS = {
  email: 'メール', phone: '電話', fax: 'FAX', document: '書面', other: 'その他',
};

const STATUS_CLS = {
  draft: 'bg-zinc-100 text-zinc-600 border-zinc-200',
  quoted: 'bg-sky-50 text-sky-700 border-sky-200',
  approval_pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-600 border-red-200',
  sent_to_seizo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  in_production: 'bg-blue-50 text-blue-700 border-blue-200',
  qc_pending: 'bg-violet-50 text-violet-700 border-violet-200',
  qc_passed: 'bg-teal-50 text-teal-700 border-teal-200',
  ready_to_ship: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  partially_shipped: 'bg-amber-50 text-amber-700 border-amber-200',
  shipped: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  completion_pending: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  completed: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  cancelled: 'bg-zinc-100 text-zinc-400 border-zinc-200',
};

export const STATUS_LABELS = {
  draft: '下書き', quoted: '見積発行済', approval_pending: '客先承認待ち',
  approved: '客先承認済', rejected: '客先却下', sent_to_seizo: '製造依頼済',
  in_production: '製造中', qc_pending: '検査待ち', qc_passed: '検査合格',
  ready_to_ship: '出荷準備完了', partially_shipped: '一部出荷', shipped: '出荷済',
  completion_pending: '売上確定待ち', completed: '完了', cancelled: '中止',
};

export const WO_STATUS_CLS = {
  planned: 'bg-zinc-100 text-zinc-600 border-zinc-200',
  released: 'bg-sky-50 text-sky-700 border-sky-200',
  in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
  on_hold: 'bg-amber-50 text-amber-700 border-amber-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-red-50 text-red-500 border-red-200',
};

export function StatusBadge({ status, label, className = '' }) {
  return (
    <span data-testid={`mkt-status-badge-${status}`}
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold whitespace-nowrap ${STATUS_CLS[status] || STATUS_CLS.draft} ${className}`}>
      {label || STATUS_LABELS[status] || status}
    </span>
  );
}

// 客先 searchable combobox (Popover + Command, client-side filter)
export function CustomerPicker({ customers, value, onChange, placeholder = '客先を選択', allowClear = false, testid = 'mkt-customer-picker' }) {
  const [open, setOpen] = useState(false);
  const picked = useMemo(() => (customers || []).find((c) => c.id === value), [customers, value]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`${testid}-trigger`}
          className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-ring">
          <span className={`truncate ${picked ? 'text-zinc-900' : 'text-zinc-400'}`}>
            {picked ? <>{picked.name}{picked.code && <span className="ml-1.5 font-mono text-[11px] text-zinc-400">{picked.code}</span>}</> : placeholder}
          </span>
          <CaretDown size={13} className="shrink-0 text-zinc-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="客先名・かな・コードで検索" data-testid={`${testid}-search`} />
          <CommandList className="max-h-60">
            <CommandEmpty>該当なし</CommandEmpty>
            {allowClear && (
              <CommandItem value="__all__" onSelect={() => { onChange(null); setOpen(false); }} data-testid={`${testid}-clear`}>
                <span className="text-zinc-500">すべての客先</span>
              </CommandItem>
            )}
            {(customers || []).map((c) => (
              <CommandItem key={c.id} value={`${c.name} ${c.kana || ''} ${c.code || ''}`}
                onSelect={() => { onChange(c.id, c); setOpen(false); }} data-testid={`${testid}-opt-${c.id}`}>
                <Check size={13} className={`mr-2 shrink-0 ${c.id === value ? 'opacity-100 text-fuchsia-600' : 'opacity-0'}`} />
                <span className="truncate">{c.name}</span>
                {c.code && <span className="ml-auto pl-3 font-mono text-[11px] text-zinc-400">{c.code}</span>}
                {c.status === 'inactive' && <span className="ml-2 text-[10px] text-red-400">無効</span>}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
